<template>

  <div>

    <el-row :gutter="12">

      <el-col :sm="24" :md="24" :lg="4" align="left">
        <h3><i class="fa fa-plug" />&nbsp;{{utilLabel + ' Report'}}</h3>
      </el-col>

      <el-col :sm="24" :md="12" :lg="16" align="left">

        <el-form inline class="filter-row">
          <select-month v-model="months" placeholder="All months" clearable />
          <select-year v-model="years" :back="10" />
        </el-form>

      </el-col>

      <el-col :sm="24" :md="12" :lg="4" align="right">
        <el-tooltip :disabled="exportEnabled" content="No records to export for the selected filter" placement="top">
          <span style="display: inline-block;">
            <download-button
              :url="exportUrl"
              :disabled="!exportEnabled"
              icon="fa fa-file-excel"
              type="primary">Excel</download-button>
          </span>
        </el-tooltip>
      </el-col>

    </el-row>


    <xolas-table
    ref="utiltable"
    table-ref="utiltable"
    url="/report/utility/details/"
    :fields="fields"
    :params="tableParams"
    :show-refresh="false"
    sort-field="sorting|DESC,siteName|ASC,sharerNo|ASC"
    :show-search="false"
    @total-changed="tableTotal = $event">
  </xolas-table>

  </div>

</template>

<script>

import DateMixins from '@/mixins/DateMixins'
import NumberMixins from '@/mixins/NumberMixins'

import DownloadButton from '@/components/atoms/files/DownloadButton'


export default {
  mixins: [DateMixins, NumberMixins],

  components: {
    DownloadButton
  },

  computed: {
    tableParams () {
      const p = { year: this.years }
      if (this.months) p.month = this.months
      return p
    },
    exportUrl () {
      let qs = `year=${this.years}`
      if (this.months) qs += `&month=${this.months}`
      return `/export/utility?${qs}`
    },
    exportEnabled () {
      return this.tableTotal > 0
    }
  },

  watch: {
    tableParams (newParams) {
      if (!this.$refs.utiltable) return
      // Pre-disable Excel during the in-flight reload — the new total will arrive
      // via @total-changed once the request resolves.
      this.tableTotal = 0
      // Mutate XolasTable's moreParams synchronously so its render queues this
      // tick; defer refresh() one tick so the new params have actually
      // propagated by the time fetch() reads this.moreParams.
      // Same pattern XolasTable.vue itself uses at onFilterSet / onFilterReset.
      this.$refs.utiltable.moreParams = newParams
      this.$nextTick(() => {
        if (!this.$refs.utiltable) return
        this.$refs.utiltable.refresh()
      })
    }
  },

  methods: {
    formatCabin (value) {
      if (value && value === 1) {
        return '<i class="fa fa-home text-muted"></i>'
      } else {
        return null
      }
    },
    formatMeter (value) {
      if (value || value === 1) {
        return '<i class="fa fa-plug text-primary"></i>'
      } else {
        return null
      }
    }
  },

  data () {
    return {
      years: this.year(),
      months: null,
      tableTotal: 0,
      sortOrder: [
        {
          field: 'sorting',
          direction: 'desc'
        },
        {
          field: 'siteName',
          direction: 'asc'
        },
        {
          field: 'sharerNo',
          direction: 'desc'
        }
      ],
      thisYear: this.year(),
      utilLabel: this.getUtilLabel(this.siteData),
      // minWidth hints mirror the pre-migration vuetable-2 render, measured at
      // 1504px viewport (.context/xol-234-baselines/metrics.json,
      // finance-utility-report[0]) — el-table distributes un-sized columns
      // equally, which does not match the legacy content-based auto layout.
      // 'year'/'month' baseline widths are 0 (hidden-sm-and-up at 1504px,
      // matching the legacy CSS-hidden state) — no minWidth added.
      fields: [
        {
          name: 'year',
          title: 'Yr',
          sortField: 'year',
          titleClass: 'hidden-sm-and-up',
          dataClass: 'hidden-sm-and-up'
        },
        {
          name: 'month',
          title: 'M',
          sortField: 'month',
          titleClass: 'hidden-sm-and-up',
          dataClass: 'hidden-sm-and-up'
        },
        {
          name: 'period',
          title: 'Date',
//          sortField: 'period',
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          minWidth: 53
        },
        {
          name: 'siteName',
          title: 'Site Name',
//          sortField: 'siteName'
          minWidth: 116
        },
        {
          name: 'sharers',
          title: 'Sharers',
          callback: this.formatInteger,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          minWidth: 86
        },
        {
          name: 'total',
          title: 'Total',
          callback: this.formatNumber,
          dataClass: 'text-bold',
          minWidth: 57
        },
        {
          name: 'sharerNo',
          title: '#',
//          sortField: 'sharerNo',
          titleClass: 'hidden-md-and-down',
          dataClass: 'hidden-md-and-down',
          minWidth: 17
        },
        {
          name: 'shortName',
          title: 'Telco',
          dataClass: 'text-muted',
          minWidth: 63
        },
        {
          name: 'telcoSiteId',
          title: 'Telco Site Id',
          titleClass: 'hidden-md-and-down',
          dataClass: 'text-muted hidden-md-and-down',
          minWidth: 139
        },
        {
          name: 'cabin',
          title: 'Cabin',
          callback: this.formatCabin,
          titleClass: 'hidden-md-and-down',
          dataClass: 'text-muted hidden-md-and-down',
          minWidth: 64
        },
        {
          name: 'ownMeter',
          title: 'Own Meter',
          callback: this.formatMeter,
          titleClass: 'hidden-md-and-down',
          dataClass: 'text-muted text-bold hidden-md-and-down',
          minWidth: 123
        },
        {
          name: 'ratio',
          title: 'Ratio %',
          callback: this.formatPercent,
          dataClass: 'text-warning',
          minWidth: 82
        },
        {
          name: 'sharing',
          title: 'Sharing',
          callback: this.formatNumber,
          dataClass: 'text-primary pull-right',
          minWidth: 86
        }
      ]
    }
  }
}

</script>

<style scoped>
.filter-row .el-form-item { min-width: 140px; }
.filter-row .el-form-item + .el-form-item { margin-left: 12px; }
</style>

<!-- Dev-parity: zero horizontal cell padding + tight line-height to match the
     legacy vuetable-2 render (dev <td> uses `12px 0`). -->
<style scoped>
::v-deep .el-table th .cell,
::v-deep .el-table td .cell { padding-left: 0; padding-right: 0; }
::v-deep .el-table th .cell { white-space: normal; }
::v-deep .el-table th .cell,
::v-deep .el-table td .cell { line-height: 1.25; }
</style>
